import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentNugget } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { setCurrentDialogNugget } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { SearchPage } from './SearchPage';
import { usePagination } from '../hooks/usePagination';
import { useSearchActions } from '../hooks/useSearchActions';
import { useSearchPageCategoryFilterMap } from '../hooks/useSearchPageCategoryFilterMap';
import { SEARCH_PAGE_CATEGORIES, SearchPageCategory } from '../search.types';

export const SearchPageContainer = () => {
  const { subPageCategory } = useParams();
  // Ensure category is a valid SearchPageCategory
  const currentPageCategory = (subPageCategory ||
    SEARCH_PAGE_CATEGORIES.DEFAULT) as SearchPageCategory;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const searchState = useAppSelector((state) => state.search);
  const searchParams = searchState.pageSearchParams[currentPageCategory];
  const currentSearchTerm = searchParams?.searchTerm || '';
  const selectedFilters = searchParams?.selectedFilters || [];
  const searchResults = searchParams?.searchResults;

  const completedNuggetIds = useAppSelector((state) => state.user.user?.completed_nugget_ids);
  const { careerPaths, industries, careerCategories } = useAppSelector((state) => state.careerPath);

  // get categories map, like what categories are available for the current search page
  const searchPageCategoryFilterMap = useSearchPageCategoryFilterMap({
    careerPaths,
    industries,
    careerCategories,
  });

  // get pagination controls
  const { handleNextPage, handlePrevPage, handleSelectPage, clearSearch } = usePagination({
    currentSearchTerm,
    selectedFilters,
    currentPageCategory,
    currentPage: searchResults?.page || 1,
  });

  const { handleSettingSelectedItems, handleUpdateSearchTerm, initiateSearch } = useSearchActions({
    currentPageCategory,
    searchParams,
    currentSearchTerm,
    selectedFilters,
    hasInitialSearchBeenMade: searchParams?.hasInitialSearchBeenMade,
  });

  useEffect(() => {
    // whenever we initially land on a search page, we initiate a search just once to get the initial filtered and alphabetized results.
    // For the default search, we don't make an initial search (at this point)
    if (
      !searchParams?.hasInitialSearchBeenMade &&
      currentPageCategory !== SEARCH_PAGE_CATEGORIES.DEFAULT
    ) {
      initiateSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageCategory]);

  const handlePanelNuggetClick = (nugget: ContentNugget) => {
    if (nugget.variant === 'competency') {
      navigate(`${APP_ROUTE.COMPETENCY}/${nugget.slug}`);

      return;
    }

    if (nugget.variant === 'institution' && nugget.college_info) {
      navigate(`${APP_ROUTE.UNIVERSITY}/${nugget.college_info.id}`);

      return;
    }

    if (nugget.variant === 'employer' && nugget.employer_info) {
      navigate(`${APP_ROUTE.EMPLOYER}/${nugget.employer_info.slug}`);

      return;
    }

    dispatch(setCurrentDialogNugget(nugget));
  };

  const categories =
    searchPageCategoryFilterMap[currentPageCategory || SEARCH_PAGE_CATEGORIES.DEFAULT] ||
    searchPageCategoryFilterMap[SEARCH_PAGE_CATEGORIES.DEFAULT];

  return (
    <SearchPage
      categories={categories}
      searchResults={searchResults}
      isSearching={searchState.loading}
      completedNuggetIds={completedNuggetIds}
      currentSearchTerm={currentSearchTerm}
      selectedItems={selectedFilters}
      currentPageCategory={currentPageCategory}
      handlePanelNuggetClick={handlePanelNuggetClick}
      setSelectedItems={handleSettingSelectedItems}
      handleUpdateSearchTerm={handleUpdateSearchTerm}
      handleNextPage={handleNextPage}
      handlePrevPage={handlePrevPage}
      initiateSearch={initiateSearch}
      clearSearch={clearSearch}
      handlePageChange={handleSelectPage}
    />
  );
};
